import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article?.categorySlug && a.slug !== slug)
    .slice(0, 3);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} – JavBus 使用指南`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt);
      }
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">您访问的文章可能已被移除或链接失效</p>
            <Link to="/" className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium">
              <i className="ri-arrow-left-line mr-1"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: 'JavBus 使用指南',
    },
    publisher: {
      '@type': 'Organization',
      name: 'JavBus 使用指南',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
            <div className="pb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-foreground-400 text-xs">{article.date}</span>
                <span className="text-foreground-400 text-xs">{article.readTime}</span>
              </div>
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap gap-1.5">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-500 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <article className="bg-white rounded-lg p-6 md:p-8">
              <div
                className="prose prose-sm max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                  prose-h2:text-lg prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-base prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:my-4 prose-ul:list-disc prose-ul:pl-5
                  prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-5
                  prose-li:text-foreground-700 prose-li:text-sm prose-li:leading-relaxed prose-li:mb-1
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  [&_ul>li]:pl-1 [&_ol>li]:pl-1"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-12">
                <h3 className="font-heading font-semibold text-foreground-900 text-base mb-5">
                  相关文章推荐
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="bg-white rounded-lg p-4 hover:bg-background-100 transition-colors group cursor-pointer"
                    >
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                        {ra.category}
                      </span>
                      <h4 className="font-heading font-medium text-foreground-900 text-sm mt-2 mb-1.5 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <span className="text-foreground-400 text-xs">{ra.date}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}